import { type Option } from "@/components/ui/autocomplete";
import ReactDOM from 'react-dom';
import React from 'react';
import { Input } from '@/components/ui/input';
import { X, Check } from 'lucide-react';
import { Button } from "./button";

export type { Option } from "@/components/ui/autocomplete";

interface AutoCompletePortalProps {
  options: Option[];
  emptyMessage: string;
  value?: Option;
  onValueChange?: (value: Option) => void;
  isLoading?: boolean;
  input: string;
  onInputChanged: (input: string) => void;
  shouldFilter?: boolean;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  onClear?: () => void;
}

// Standalone portal-based autocomplete that renders dropdown in a portal (not clipped)
export function AutoCompletePortal(props: AutoCompletePortalProps) {
  const {
    options,
    placeholder,
    emptyMessage,
    value,
    onValueChange,
    isLoading = false,
    input,
    onInputChanged,
    shouldFilter = true,
    className,
    disabled,
    onClear,
    ...inputProps
  } = props;

  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isOpen, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<Option | undefined>(value as Option);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [vwOverflow, setVwOverflow] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);

  // Internal filtering logic
  const filteredOptions = shouldFilter
    ? options.filter(option =>
        option.label.toLowerCase().includes(input.toLowerCase())
      )
    : options;

  React.useEffect(() => {
    if (!isOpen) setHighlightedIndex(-1);
  }, [isOpen, input]);

  React.useEffect(() => {
    if (!isLoading && isOpen) {
      if (contentRef.current) {
        const content = contentRef.current;
        const rect = content.getBoundingClientRect();
        const vwHeight = document.body.getBoundingClientRect().height;
        const contentHeight = rect.height + rect.top;
        if (contentHeight > vwHeight) {
          setVwOverflow(true);
        }
      }
    }
  }, [isLoading, isOpen]);

  const handleSelectOption = React.useCallback(
    (selectedOption: Option) => {
      onInputChanged(selectedOption.label);
      setSelected(selectedOption);
      onValueChange?.(selectedOption);
      setTimeout(() => {
        inputRef?.current?.blur();
      }, 0);
    },
    [onValueChange, onInputChanged]
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const inputEl = inputRef.current;
      if (!inputEl) return;
      if (!isOpen) setOpen(true);
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex(idx => {
          const next = idx + 1;
          return next >= filteredOptions.length ? 0 : next;
        });
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex(idx => {
          const prev = idx - 1;
          return prev < 0 ? filteredOptions.length - 1 : prev;
        });
      } else if (event.key === "Enter") {
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        } else if (inputEl.value !== "") {
          const optionToSelect = filteredOptions.find(option => option.label === inputEl.value);
          if (optionToSelect) {
            setSelected(optionToSelect);
            onValueChange?.(optionToSelect);
          }
        }
      } else if (event.key === "Escape") {
        inputEl.blur();
      }
    },
    [isOpen, filteredOptions, onValueChange, highlightedIndex, handleSelectOption]
  );

  const handleBlur = React.useCallback(() => {
    setOpen(false);
    if (selected) onInputChanged(selected.label);
  }, [selected, onInputChanged]);

  // Calculate dropdown position
  const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties>({});
  React.useLayoutEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        left: rect.left,
        top: rect.bottom + window.scrollY,
        width: rect.width,
        position: 'absolute',
        zIndex: 9999,
      });
    }
  }, [isOpen]);

  const dropdown = (
    <div
      className={
        `animate-in fade-in-0 zoom-in-95 absolute ${vwOverflow ? "bottom-[50px]" : "top-0"} z-[9999] w-full rounded-xl max-h-60 bg-white outline-none border border-slate-200/70` +
        (isOpen ? " block" : " hidden")
      }
      style={dropdownStyle}
    >
      <div
        ref={contentRef}
        className="rounded-lg ring-slate-200/70 border border-slate-200/70 max-h-60 overflow-y-auto"
      >
        {isLoading ? (
          <div className="p-1">
            <div className="h-8 w-full bg-gray-100 animate-pulse rounded" />
          </div>
        ) : null}
        {filteredOptions.length > 0 && !isLoading ? (
          <div>
            {filteredOptions.map((option, idx) => {
              const isSelected = selected?.value === option.value;
              const isHighlighted = idx === highlightedIndex;
              return (
                <div
                  key={option.value}
                  onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={() => handleSelectOption(option)}
                  className={
                    "flex w-full items-center text-sm gap-2 cursor-pointer px-4 py-2 " +
                    (isHighlighted ? "bg-blue-100" : "hover:bg-gray-100") +
                    (!isSelected ? " pl-8" : "")
                  }
                >
                  {isSelected ? <Check className="w-4" /> : null}
                  {option.label}
                </div>
              );
            })}
          </div>
        ) : null}
        {!isLoading && filteredOptions.length === 0 ? (
          <div className="select-none rounded-sm px-2 py-3 text-center text-sm">
            {emptyMessage}
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <div
      className={"w-full bg-white border rounded-lg relative flex items-center " + (className || "")}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      <Input
        ref={inputRef}
        value={input}
        onChange={e => !disabled && onInputChanged(e.target.value)}
        onBlur={handleBlur}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="text-sm w-full text-left h-7 px-2 pr-8 border-[0.5px] border-slate-200/70"
        disabled={disabled}
        {...inputProps}
      />
      {input && (
        <button
          type="button"
          onClick={() => {
            onInputChanged("");
            onClear?.();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 focus:outline-none"
          tabIndex={-1}
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      )}
      {typeof window !== 'undefined' && isOpen && inputRef.current
        ? ReactDOM.createPortal(dropdown, document.body)
        : null}
    </div>
  );
} 